import { useState } from 'react';
import { Button, Checkbox, Dropdown, Icon } from 'semantic-ui-react';

import { useIsAdmin } from '../../../../hooks/isAdmin';
import { useIsCenterManager } from '../../../../hooks/isCenterManager';

interface Props {
    missingConsent: boolean;
    canLogin: boolean | null;
    hasAdvancedFilter: boolean;
    onMissingConsentChange: (next: boolean) => void;
    onCanLoginChange: (next: boolean | null) => void;
    className?: string;
}

/**
 * The two follow-up worklists — people still to take through registration, and people who have
 * never set a password — behind a disclosure.
 *
 * **Shown only to administrators and centre managers**, and the gate lives here rather than at
 * each call site so the two People views cannot drift apart on who sees what. These are
 * organisation-wide worklists: a teacher's job is the people in their own classes, and a
 * control that answers "who across the whole place is missing a waiver" is not theirs to act
 * on.
 *
 * **The gate narrows the UI, never the data.** `narrowToCanLogin` and `narrowToMissingConsent`
 * on the server filter whatever set the caller was already permitted to see and never
 * contribute to it, so a centre manager using these still sees only their own people — fewer
 * of them, never more.
 *
 * Search and tags stay outside: those are what almost everybody wants almost every time, and
 * putting them behind a click would cost far more than it saved.
 */
const AdvancedFilters = ({
    missingConsent,
    canLogin,
    hasAdvancedFilter,
    onMissingConsentChange,
    onCanLoginChange,
    className,
}: Props) => {
    const isAdmin = useIsAdmin();
    const isCenterManager = useIsCenterManager();

    /**
     * Starts open when a filter is already on, so arriving by a shared link or the back button
     * never hides the very filter producing the list on screen.
     */
    const [showAdvanced, setShowAdvanced] = useState(hasAdvancedFilter);

    if (!isAdmin && !isCenterManager) {
        return null;
    }

    return (
        <>
            <div className={className}>
                <Button
                    basic
                    size="small"
                    type="button"
                    onClick={() => setShowAdvanced((open) => !open)}
                    aria-expanded={showAdvanced}
                >
                    <Icon name={showAdvanced ? 'chevron up' : 'chevron down'} />
                    Advanced filters
                    {/* The count is what stops a collapsed panel hiding the reason a list
                        looks short. */}
                    {!showAdvanced && hasAdvancedFilter && ' (1 on)'}
                </Button>
            </div>

            {showAdvanced && (
                <div className={className}>
                    {/* The registration follow-up list. People with no health & safety waiver
                        or no data-collection consent are exactly the people still to take
                        through the new registration form — so the gap itself is the worklist,
                        rather than a separate flag someone has to remember to tick. */}
                    <Checkbox
                        toggle
                        label="Missing H&S / data consent"
                        checked={missingConsent}
                        onChange={(_, data) => onMissingConsentChange(!!data.checked)}
                    />
                    {/* Three states, so "any" is distinct from "cannot". The useful one is
                        **Cannot sign in**: a membership with no password is what an unaccepted
                        invitation, or an imported record, looks like from the outside. */}
                    <Dropdown
                        selection
                        clearable
                        placeholder="Account access: any"
                        options={[
                            { key: 'yes', value: 'yes', text: 'Can sign in' },
                            { key: 'no', value: 'no', text: 'Cannot sign in' },
                        ]}
                        value={canLogin === null ? '' : (canLogin ? 'yes' : 'no')}
                        onChange={(_, { value }) =>
                            onCanLoginChange(value === 'yes' ? true : value === 'no' ? false : null)}
                        style={{ marginTop: '0.75em' }}
                    />
                </div>
            )}
        </>
    );
};

export { AdvancedFilters };
